/** Minimal LCS line/word diff for agent version compare (no deps). */

export type DiffSide = "eq" | "del" | "add" | "mod";

export type DiffLine = {
  side: DiffSide;
  text: string;
  /** Word-level tokens when side is mod (left or right view). */
  tokens?: Array<{ t: "eq" | "del" | "add"; text: string }>;
};

function splitLines(s: string): string[] {
  if (!s) return [];
  return s.replace(/\r\n/g, "\n").split("\n");
}

function splitWords(s: string): string[] {
  // Keep whitespace as separate tokens so layout stays readable
  return s.split(/(\s+)/).filter((x) => x.length > 0);
}

function lcsTable(a: string[], b: string[]): number[][] {
  const n = a.length;
  const m = b.length;
  const dp: number[][] = Array.from({ length: n + 1 }, () => Array(m + 1).fill(0));
  for (let i = n - 1; i >= 0; i -= 1) {
    for (let j = m - 1; j >= 0; j -= 1) {
      dp[i][j] = a[i] === b[j] ? dp[i + 1][j + 1] + 1 : Math.max(dp[i + 1][j], dp[i][j + 1]);
    }
  }
  return dp;
}

function wordTokens(left: string, right: string): {
  left: Array<{ t: "eq" | "del"; text: string }>;
  right: Array<{ t: "eq" | "add"; text: string }>;
} {
  const a = splitWords(left);
  const b = splitWords(right);
  const dp = lcsTable(a, b);
  const L: Array<{ t: "eq" | "del"; text: string }> = [];
  const R: Array<{ t: "eq" | "add"; text: string }> = [];
  let i = 0;
  let j = 0;
  while (i < a.length && j < b.length) {
    if (a[i] === b[j]) {
      L.push({ t: "eq", text: a[i] });
      R.push({ t: "eq", text: b[j] });
      i += 1;
      j += 1;
    } else if (dp[i + 1][j] >= dp[i][j + 1]) {
      L.push({ t: "del", text: a[i] });
      i += 1;
    } else {
      R.push({ t: "add", text: b[j] });
      j += 1;
    }
  }
  while (i < a.length) {
    L.push({ t: "del", text: a[i] });
    i += 1;
  }
  while (j < b.length) {
    R.push({ t: "add", text: b[j] });
    j += 1;
  }
  return { left: L, right: R };
}

/** Pair aligned left/right rows for side-by-side red-green rendering. */
export type DiffRow = {
  left: DiffLine | null;
  right: DiffLine | null;
};

export function diffSideBySide(leftText: string, rightText: string): DiffRow[] {
  const a = splitLines(leftText);
  const b = splitLines(rightText);
  if (!a.length && !b.length) return [{ left: { side: "eq", text: "—" }, right: { side: "eq", text: "—" } }];
  const dp = lcsTable(a, b);
  const rows: DiffRow[] = [];
  let i = 0;
  let j = 0;
  while (i < a.length || j < b.length) {
    if (i < a.length && j < b.length && a[i] === b[j]) {
      rows.push({
        left: { side: "eq", text: a[i] },
        right: { side: "eq", text: b[j] },
      });
      i += 1;
      j += 1;
    } else if (i < a.length && j < b.length && dp[i + 1][j] < dp[i][j + 1]) {
      // Prefer consume right as add when LCS says so — but for "changed" lines,
      // if both have remaining unequal content, emit a mod pair.
      // Heuristic: if next equal is far, treat as modification when both present.
      const lookA = a[i];
      const lookB = b[j];
      const equalAhead =
        a.slice(i + 1).includes(lookB) || b.slice(j + 1).includes(lookA);
      if (!equalAhead) {
        const wt = wordTokens(lookA, lookB);
        rows.push({
          left: { side: "mod", text: lookA, tokens: wt.left },
          right: { side: "mod", text: lookB, tokens: wt.right },
        });
        i += 1;
        j += 1;
      } else if (dp[i + 1][j] >= dp[i][j + 1]) {
        rows.push({ left: { side: "del", text: a[i] }, right: null });
        i += 1;
      } else {
        rows.push({ left: null, right: { side: "add", text: b[j] } });
        j += 1;
      }
    } else if (i < a.length && (j >= b.length || dp[i + 1][j] >= dp[i][j + 1])) {
      rows.push({ left: { side: "del", text: a[i] }, right: null });
      i += 1;
    } else if (j < b.length) {
      rows.push({ left: null, right: { side: "add", text: b[j] } });
      j += 1;
    } else {
      break;
    }
  }
  return rows;
}

export function hasDiff(leftText: string, rightText: string): boolean {
  return String(leftText ?? "") !== String(rightText ?? "");
}
